import type * as React from "react";
import { Skeleton } from "@/components/ui/atoms/skeleton";
import { SuggestionCardSkeleton } from "@/components/ui/molecules/suggestion-card";
import {
  ChatTemplate,
  ChatTemplateFooter,
  ChatTemplateHeader,
  ChatTemplateHeaderGroup,
  ChatTemplateOverview,
  ChatTemplateSuggestions,
} from "@/components/ui/templates/chat-template";
import { cn } from "@/lib/utils";

// ─── ChatHeaderSkeleton ──────────────────────────────────────────────────────

export type ChatHeaderSkeletonProps = React.ComponentProps<"header">;

export function ChatHeaderSkeleton({
  className,
  ...props
}: ChatHeaderSkeletonProps) {
  return (
    <ChatTemplateHeader className={className} {...props}>
      <ChatTemplateHeaderGroup>
        <Skeleton className="h-7 w-7 rounded-md" />
      </ChatTemplateHeaderGroup>
      <div className="flex-1" />
      <ChatTemplateHeaderGroup>
        <Skeleton className="h-9 w-9 rounded-md sm:w-[88px]" />
      </ChatTemplateHeaderGroup>
    </ChatTemplateHeader>
  );
}

// ─── ChatBodySkeleton ────────────────────────────────────────────────────────

export type ChatBodySkeletonProps = React.ComponentProps<"div">;

export function ChatBodySkeleton({
  className,
  ...props
}: ChatBodySkeletonProps) {
  return (
    <div
      className={cn(
        "flex min-w-0 flex-1 flex-col gap-6 overflow-y-auto pt-4",
        className
      )}
      {...props}
    >
      <ChatTemplateOverview>
        <Skeleton className="mx-auto mb-2 h-8 w-40 rounded-lg sm:mb-4 sm:h-9 sm:w-48 md:h-[44px] md:w-56" />
        <Skeleton className="mx-auto mb-4 h-7 w-56 rounded-lg sm:mb-8 sm:h-8 sm:w-72 md:h-[44px] md:w-96" />
      </ChatTemplateOverview>
    </div>
  );
}

// ─── ChatFooterSkeleton ──────────────────────────────────────────────────────

export type ChatFooterSkeletonProps = React.ComponentProps<"footer">;

export function ChatFooterSkeleton({
  className,
  ...props
}: ChatFooterSkeletonProps) {
  return (
    <ChatTemplateFooter className={className} {...props}>
      <ChatTemplateSuggestions>
        {Array.from({ length: 5 }).map((_, i) => (
          <div
            className={cn(
              "col-span-12 sm:col-span-6",
              i < 3 ? "md:col-span-4" : "md:col-span-6",
              i > 1 && "hidden sm:block"
            )}
            key={`suggestion-skeleton-${i.toString()}`}
          >
            <SuggestionCardSkeleton />
          </div>
        ))}
      </ChatTemplateSuggestions>
      <div className="rounded-lg border bg-background p-2 shadow-xs">
        <Skeleton className="h-[44px] w-full rounded-md" />
        <div className="flex justify-end px-2 pt-2 pb-2">
          <Skeleton className="h-9 w-9 rounded-md" />
        </div>
      </div>
    </ChatTemplateFooter>
  );
}

// ─── ChatSidebarSkeleton ─────────────────────────────────────────────────────

export type ChatSidebarSkeletonProps = React.ComponentProps<"div">;

export function ChatSidebarSkeleton({
  className,
  ...props
}: ChatSidebarSkeletonProps) {
  return (
    <div
      className={cn(
        "flex w-(--sidebar-width) shrink-0 flex-col bg-sidebar p-2",
        className
      )}
      {...props}
    >
      <div className="flex flex-col gap-6 pb-2">
        <div className="flex items-center px-2 pt-2">
          <Skeleton className="h-5 w-20" />
        </div>
        <Skeleton className="h-9 w-full rounded-md" />
      </div>

      <div className="flex-1 overflow-hidden py-2">
        <div className="px-2 py-1">
          <Skeleton className="mb-2 h-3 w-12" />
        </div>
        <div className="flex flex-col gap-0.5">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              className="flex h-8 items-center gap-2 rounded-md px-2"
              key={`skeleton-row-${i.toString()}`}
            >
              <Skeleton
                className="h-4 flex-1"
                style={{ maxWidth: `${55 + ((i * 17) % 35)}%` }}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="mt-auto p-2">
        <div className="flex items-center gap-2 rounded-lg p-2">
          <Skeleton className="h-8 w-8 rounded-full" />
          <div className="flex flex-1 flex-col gap-1">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-2.5 w-32" />
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── ChatContentSkeleton (composed header + body + footer) ───────────────────

export type ChatContentSkeletonProps = React.ComponentProps<"div">;

export function ChatContentSkeleton({
  className,
  ...props
}: ChatContentSkeletonProps) {
  return (
    <ChatTemplate className={className} {...props}>
      <ChatHeaderSkeleton />
      <ChatBodySkeleton />
      <ChatFooterSkeleton />
    </ChatTemplate>
  );
}
